import { store } from '@/store/store'
import { citatorStateAddCaseStatus, citatorStateSetError, citatorStateSetLoading } from '../store/citator-state.slice'
import { getAuth } from 'firebase/auth'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { nanoid } from 'nanoid'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kCitatorGetCaseStatusApiPathV2 } from '@/constants/constants-api-paths'
import * as Sentry from '@sentry/react'
import { caseStatusResponseSchema } from '../schema/case-status-schema'

/**
 * Get Case Status
 *
 * Fetches case status from the citator API and updates Redux with the result.
 *
 * @param parent_id - The parent ID of the case
 */
export async function getCaseStatus(parent_id: string) {
  if (!parent_id) throw new Error('Citator requires parent_id')

  // Synchronous dedupe: if a fetch is already in-flight, or a successful result is
  // already cached, bail out. This covers the case where multiple components mount
  // in the same render cycle and each check Redux before either dispatch lands.
  // Retries on error are still allowed (existing error state does not block).
  const existing = store.getState().citatorState.caseStatuses[parent_id]
  if (existing && (existing.loading || (existing.data && !existing.error))) return

  // Set loading status
  store.dispatch(citatorStateSetLoading({ parent_id, loading: true }))
  store.dispatch(citatorStateSetError({ parent_id, error: false }))

  const sentry_transaction_id = nanoid()
  const apiUrl = kPaxtonAppApiRESTBaseUrl() + `/${kCitatorGetCaseStatusApiPathV2}/${parent_id}`

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw Error(kErrorCodeAuthError)

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentry_transaction_id,
        Authorization: `Bearer ${token}`,
      },
      method: 'GET',
    }

    const response = await fetch(apiUrl, requestOptions)
    const data = await response.json()

    if (response.status === 200) {
      validateAndStoreResponse(parent_id, data)
      return
    }

    store.dispatch(citatorStateSetError({ parent_id, error: true }))
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentry_transaction_id })

      Sentry.captureException(new Error(`Case Status Fetch error - status: ${response.status}, detail: ${data.detail}`), {
        extra: { requestUrl: apiUrl, requestOptions: requestOptions, response: data, onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled },
      })
    })
  } catch (error) {
    console.error('Failed to fetch case status: ', error)
    store.dispatch(citatorStateSetError({ parent_id, error: true }))
  } finally {
    store.dispatch(citatorStateSetLoading({ parent_id, loading: false }))
  }
}

/**
 * Validate and store response
 * @param parent_id the case we are fetching the status for
 * @param data the data we are validating
 * @returns {void}
 */
function validateAndStoreResponse(parent_id: string, data: unknown): void {
  // Validate response
  const responseParsed = caseStatusResponseSchema.safeParse(data)
  if (!responseParsed.success) {
    console.error(responseParsed.error)
    store.dispatch(citatorStateSetError({ parent_id, error: true }))
    return
  }

  // Dispatch response
  store.dispatch(citatorStateAddCaseStatus(responseParsed.data))
}
